import React, { useRef } from 'react';
import { Navigation } from 'swiper/modules';
import 'swiper/css/navigation';
import styled from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  brandColours,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Container, Heading, Slider, Svg } from './ui';
import ReviewCard from './ReviewCard';
import arrowIcon from '../images/arrow-icon.svg';

const StyledTestimonials = styled.section`
  ${sectionPaddings()};
  background-color: ${standardColours.lighterGrey};
`;

const StyledHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StyledArrows = styled.div`
  display: flex;
  align-items: center;
`;

const StyledArrow = styled.button`
  margin: 5px;
  padding: 10px;
  height: 38px;
  width: 38px;
  background: none;
  border: 1px solid ${standardColours.darkGrey};
  border-radius: 50%;
  cursor: pointer;
  ${standardTransition('background-color')};

  ${minBreakpointQuery.smedium`
    margin: 10px;
  `}

  ${minBreakpointQuery.large`
    margin: 15px;
  `}

  svg {
    height: 16px;
    width: 16px;
    fill: ${standardColours.darkGrey};
    ${standardTransition('fill')};
  }

  span {
    ${visuallyHidden()};
  }

  &:hover {
    background-color: ${brandColours.primary};

    svg {
      fill: ${standardColours.white};
    }
  }

  &:first-child {
    transform: rotate(180deg);
  }
`;

const StyledSlider = styled(Slider)`
  margin: 0 -30px;

  .swiper-container {
    width: 100%;
  }

  .swiper-slide {
    width: auto;
  }
`;

const StyledItem = styled(ReviewCard)`
  padding: 0 30px;

  ${minBreakpointQuery.large`
    padding-top: 10px;
    padding-bottom: 10px;
  `}
`;

const Testimonials = ({ heading, items }) => {
  const nextEl = useRef(null);
  const prevEl = useRef(null);

  const sliderOptions = {
    modules: [Navigation],
    slidesPerView: 1,
    spaceBetween: 30,
    loop: true,
    breakpoints: {
      [breakpointSizes.small]: { slidesPerView: 2 },
    },
    onInit: swiper => {
      swiper.params.navigation.prevEl = prevEl.current;
      swiper.params.navigation.nextEl = nextEl.current;
      swiper.navigation.init();
      swiper.navigation.update();
    },
  };

  return (
    items.length > 0 && (
      <StyledTestimonials>
        <Container>
          <StyledHeader>
            <Heading>{heading}</Heading>
            <StyledArrows>
              <StyledArrow ref={prevEl} alt={true} aria-label="Previous">
                <Svg image={arrowIcon} />
                <span>Previous</span>
              </StyledArrow>
              <StyledArrow ref={nextEl} alt={true} aria-label="Next">
                <Svg image={arrowIcon} />
                <span>Next</span>
              </StyledArrow>
            </StyledArrows>
          </StyledHeader>
          <StyledSlider {...sliderOptions}>
            {items.map(({ id, quote, author }) => (
              <StyledItem key={id} text={quote} name={author} />
            ))}
          </StyledSlider>
        </Container>
      </StyledTestimonials>
    )
  );
};

export default Testimonials;
